import { z } from 'zod'
import i18n from 'i18next'

/**
 * Централизованные схемы валидации форм через Zod.
 *
 * Раньше валидация жила inline в каждой форме как `disabled={!x.trim() || !y.trim()}`
 * — это:
 *   • не давало внятных сообщений об ошибках (просто disabled-button);
 *   • дублировалось между save/disabled/error-render;
 *   • рассинхронизировалось с типами Student/Grade (`gender?: 'M'|'F'`,
 *     `classId: string`, и т.д.).
 *
 * Zod даёт единый источник правды:
 *   const { success, error } = studentFormSchema.safeParse(formData)
 *   if (!success) showErrors(error.flatten().fieldErrors)
 */

// message хранит i18n-ключ (а не готовый текст), чтобы язык не «замораживался»
// на этапе загрузки модуля. Перевод происходит в validateForm() при показе.
const nonEmptyString = (messageKey: string) =>
  z.string().trim().min(1, { message: messageKey })

export const studentFormSchema = z.object({
  lastName: nonEmptyString('validation.requiredLastName'),
  firstName: nonEmptyString('validation.requiredFirstName'),
  middleName: z.string().trim().optional().nullable(),
  classId: nonEmptyString('validation.requiredClassId'),
  gender: z.enum(['M', 'F']).nullable().optional(),
  birthDate: z.string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'validation.birthDateFormat' })
    .optional()
    .nullable()
    .or(z.literal('').transform(() => null)),
  medicalGroup: z.enum(['main', 'preparatory', 'special', 'exempt'])
    .nullable()
    .optional()
    .or(z.literal('').transform(() => null)),
  specialNeeds: z.string().trim().optional().nullable(),
  notes: z.string().trim().optional().nullable(),
})

export type StudentFormData = z.infer<typeof studentFormSchema>

/**
 * Удобный wrapper: возвращает { ok, errors } для UI.
 * Если ok=true — данные прошли валидацию; иначе errors — Map<field, message>.
 */
export function validateForm<T>(schema: z.ZodType<T>, data: unknown): {
  ok: boolean
  errors: Record<string, string>
} {
  const result = schema.safeParse(data)
  if (result.success) return { ok: true, errors: {} }
  const errors: Record<string, string> = {}
  for (const issue of result.error.issues) {
    const path = issue.path.join('.')
    // issue.message — i18n-ключ; i18n.t переводит его на текущий язык.
    // Если сообщение не является ключом, i18next вернёт его без изменений.
    if (!errors[path]) errors[path] = i18n.t(issue.message)
  }
  return { ok: false, errors }
}
